/**
 * Doors: placing, opening, and taking down.
 *
 * A door is two blocks, and every operation has to keep the halves in step -
 * open one and the other must follow, break one and the other must come down.
 * Getting that wrong leaves half a door floating in a wall, which is the kind
 * of thing that survives a long time because it still looks almost right.
 *
 * Pure logic over a World, like the farming rules, so all of it is testable
 * without a browser.
 */

import type { World } from "./world.js";
import {
  BlockId, isSolid, isReplaceable, isDoor, doorId, doorState,
  isShutter, shutterId, shutterState, isGate, gateId, gateState,
  type DoorFacing, type DoorWood,
} from "../block/registry.js";

/**
 * Which wall of the cell a door placed from here should fill.
 *
 * The panel goes against the edge nearest the player, so the door appears in
 * the doorway facing them rather than with its back turned.
 */
export function facingFromLook(dx: number, dz: number): DoorFacing {
  if (Math.abs(dx) > Math.abs(dz)) return dx > 0 ? 3 : 1; // west : east
  return dz > 0 ? 0 : 2;                                   // north : south
}

/**
 * The other way: the direction the player is *looking*, not facing them.
 *
 * `facingFromLook` answers "which way does this turn to face me", which is
 * right for a door, a stove and a хвіртка - you put them down and they look
 * back at you. A требушет is the opposite: "требушет повинен стріляти в ту ж
 * саму сторону в яку дивиться гравець коли його ставить", and it had the
 * door's answer, so it threw over your shoulder.
 *
 * Written as its own function rather than a `+ 2` at the call site, because
 * a `+ 2` is the kind of thing that gets copied without its reason.
 */
export function facingAwayFromLook(dx: number, dz: number): DoorFacing {
  return ((facingFromLook(dx, dz) + 2) % 4) as DoorFacing;
}

/**
 * Which way a placed stair goes up.
 *
 * A stair's facing is the side its tall half stands on - the mesher puts
 * the upper box on the facing side of the cell - so "which way it faces"
 * is "which way its back is". Two rules, and they were both wrong once:
 *
 *   - Put against the side of a block, or into the void beside one, the
 *     back goes against that block: "сходи мали стати спиною до блока, а не
 *     до повітря". The hit's normal points from the block into the cell,
 *     so the block is the other way, and that is where the tall half goes.
 *   - On a floor or a ceiling there is no block to lean on, and it climbs
 *     away from whoever put it down, the way a staircase you are walking
 *     up does.
 *
 * Both used `facingFromLook`, which answers "which way does this turn to
 * face me" - right for a door, and the reverse of what a stair wants.
 * That put the tall side toward the player and toward the air, and the
 * comment above it said the opposite, which is why it lasted.
 */
export function stairFacingFor(
  hit: { readonly nx: number; readonly ny: number; readonly nz: number },
  look: { readonly x: number; readonly z: number },
): DoorFacing {
  if (hit.ny === 0) return facingAwayFromLook(-hit.nx, -hit.nz);
  return facingAwayFromLook(look.x, look.z);
}

export type DoorPlacement = "ok" | "no-room" | "no-support" | "not-from-above";

/**
 * Can a door stand here?
 *
 * It needs two clear cells and something solid underneath. A door hung on
 * nothing would fall the moment support rules are enforced, and a door with
 * its head in a ceiling would be half a door.
 */
export function canPlaceDoor(
  world: World, x: number, y: number, z: number,
): DoorPlacement {
  if (!isReplaceable(world.getBlock(x, y, z))) return "no-room";
  if (!isReplaceable(world.getBlock(x, y + 1, z))) return "no-room";
  if (!isSolid(world.getBlock(x, y - 1, z))) return "no-support";
  return "ok";
}

/** Put both halves in, facing the same way and both shut. */
export function placeDoor(
  world: World, x: number, y: number, z: number,
  wood: DoorWood, facing: DoorFacing,
): void {
  world.setBlock(x, y, z, doorId(wood, facing, false, false));
  world.setBlock(x, y + 1, z, doorId(wood, facing, true, false));
}

/** The y of the lower half, given a cell holding either half. */
export function doorFootY(world: World, x: number, y: number, z: number): number | null {
  const here = world.getBlock(x, y, z);
  if (!isDoor(here)) return null;
  return doorState(here).upper ? y - 1 : y;
}

/**
 * Swing a door.
 *
 * Both halves are rewritten from the lower one's state, so a door whose halves
 * have somehow diverged is repaired by opening it rather than made worse.
 */
export function toggleDoor(world: World, x: number, y: number, z: number): boolean {
  const footY = doorFootY(world, x, y, z);
  if (footY === null) return false;

  const foot = world.getBlock(x, footY, z);
  if (!isDoor(foot)) return false;

  const { wood, facing, open } = doorState(foot);
  world.setBlock(x, footY, z, doorId(wood, facing, false, !open));
  world.setBlock(x, footY + 1, z, doorId(wood, facing, true, !open));
  return true;
}

/** Take both halves down. Returns false if there was no door here. */
export function removeDoor(world: World, x: number, y: number, z: number): boolean {
  const footY = doorFootY(world, x, y, z);
  if (footY === null) return false;

  world.setBlock(x, footY, z, BlockId.Air);
  // Only clear the head if it really is one - a door standing under another
  // door's foot must not take its neighbour with it.
  const head = world.getBlock(x, footY + 1, z);
  if (isDoor(head) && doorState(head).upper) {
    world.setBlock(x, footY + 1, z, BlockId.Air);
  }
  return true;
}

// --- shutters --------------------------------------------------------------

/**
 * Can a shutter hang here?
 *
 * It needs a clear cell and something solid beside it to hinge on. A shutter
 * swinging in open air is not wrong so much as nonsense, and refusing it costs
 * one check.
 */
export function canPlaceShutter(
  world: World, x: number, y: number, z: number,
): "ok" | "no-room" | "nothing-to-hang-on" {
  if (!isReplaceable(world.getBlock(x, y, z))) return "no-room";

  const beside = [
    world.getBlock(x + 1, y, z), world.getBlock(x - 1, y, z),
    world.getBlock(x, y, z + 1), world.getBlock(x, y, z - 1),
  ];
  if (!beside.some(isSolid)) return "nothing-to-hang-on";
  return "ok";
}

export function placeShutter(
  world: World, x: number, y: number, z: number,
  wood: DoorWood, facing: DoorFacing,
): void {
  world.setBlock(x, y, z, shutterId(wood, facing, false));
}

/** Swing a shutter. Returns false if there was none here. */
export function toggleShutter(
  world: World, x: number, y: number, z: number,
): boolean {
  const here = world.getBlock(x, y, z);
  if (!isShutter(here)) return false;
  const { wood, facing, open } = shutterState(here);
  world.setBlock(x, y, z, shutterId(wood, facing, !open));
  return true;
}

/**
 * Which way a thing hung on a wall should look, given the face you clicked.
 *
 * "Рушник висить в повітрі" - and he was right: the facing came from which
 * way the *player* was looking, and the placement rule only asked whether
 * *any* of the four neighbours was solid. So a рушник hung in a doorway with
 * a wall on its left came out facing the player, its back to open air, and a
 * shutter did the same.
 *
 * The face you clicked is the answer and always was: the normal points out
 * of the wall toward you, which is the way the cloth hangs. Null for a floor
 * or a ceiling - there is nothing to hang a рушник on above your head.
 */
export function facingFromFace(
  nx: number, ny: number, nz: number,
): DoorFacing | null {
  if (ny !== 0) return null;
  if (nx > 0) return 1;
  if (nx < 0) return 3;
  if (nz > 0) return 2;
  if (nz < 0) return 0;
  return null;
}

/**
 * The хвіртка in a тин, which swings the same way everything else does.
 *
 * A gateway is as tall as the fence it is cut through - two courses in a
 * село's тин - and the whole of it swings at once, the way a door's two
 * halves do. Without that, opening the bottom of a gate leaves the top shut
 * and the gateway is still a wall; and a player who had to right-click twice
 * at two different heights would reasonably conclude the thing was broken.
 *
 * Only gates facing the same way join up. Two gateways back to back in a
 * double fence are two gates, and they are not each other's other half.
 */
export function toggleGate(
  world: World, x: number, y: number, z: number,
): boolean {
  const here = world.getBlock(x, y, z);
  if (!isGate(here)) return false;
  const { facing, open } = gateState(here);
  const swing = (at: number): void =>
    world.setBlock(x, at, z, gateId(facing, !open));
  swing(y);
  for (const step of [1, -1]) {
    for (let at = y + step; ; at += step) {
      const there = world.getBlock(x, at, z);
      if (!isGate(there) || gateState(there).facing !== facing) break;
      swing(at);
    }
  }
  return true;
}

/**
 * Swing whatever hinged thing is here - door, shutter or gate.
 *
 * The game does not need to know which it clicked, and neither does the
 * player: both answer a right-click by opening.
 */
export function togglePanel(
  world: World, x: number, y: number, z: number,
): boolean {
  return toggleDoor(world, x, y, z) || toggleShutter(world, x, y, z) ||
    toggleGate(world, x, y, z);
}
